/* eslint-disable complexity */
import { ReportTotal } from '@futrli-sdk/predict';
import { createIndexFromTree, flattenTree, notEmpty } from '@futrli/shared-services';

import {
  AccountRow,
  AssumptionRow,
  CalculationRow,
  GroupRow,
  OrganisationRow,
  RowStyleOptions,
  RowType,
  SheetsAssumptionRow,
  TotalRow
} from '../../../../reports/data-grid/types';
import { CATEGORY_NAME_INDEXES, REPORT_SECTION, SIZES } from '../../../../../../core/constants';
import { ForecastTreeNode, UKForecastLayoutItem, UKForecastLayoutItemDisplayType } from '../../../types';
import { isTopLevelStaticRowType } from '../../../utils/utils';
import { ROW_KIND } from '../../../../reports/data-grid/constants';

/**
 * Converts the given layout to an array of grid rows
 * @param layout - The layout to apply
 * @param section - The report section this row belongs to (balance_sheet, Profit_and_loss etc)
 */
export const convertForecastLayoutToGridRows = (layout: UKForecastLayoutItem[], section: REPORT_SECTION) => {
  // Apply the layout to the tree
  const output: { rows: RowType[]; totalRow: TotalRow | null } = {
    rows: [],
    totalRow: null
  };

  layout.forEach(item => {
    const { nodes = [], category, displayType, totalNode, totalOptions } = item;

    if (nodes?.length || totalNode) {
      const index = createIndexFromTree(nodes, { idProp: 'ref' });

      switch (displayType) {
        case UKForecastLayoutItemDisplayType.totalWithAccounts: {
          if (totalNode) {
            output.totalRow = convertTreeNodeToCategoryTotalGridRow(totalNode, section, category, totalOptions);
            output.rows.push(...convertForecastNodesToGridRows(item, section, index, nodes));
          } else {
            output.rows.push(convertTreeNodeToCategoryTotalGridRow(nodes[0], section, category, totalOptions));
            output.rows.push(...convertForecastNodesToGridRows(item, section, index, nodes[0].children));
          }
          break;
        }
        case UKForecastLayoutItemDisplayType.calculation: {
          output.rows.push(convertTreeNodeToComparisonGridRow(nodes[0], nodes[1], section, category, item.options));
          break;
        }
        default: {
          output.rows.push(...convertForecastNodesToGridRows(item, section, index, nodes));
        }
      }
    }
  });

  return output;
};

/**
 * Converts the given tree nodes to grid rows, handling the most common node types
 * @param layoutItem - The layout item the nodes belong to
 * @param section - The report section this data belongs to
 * @param nodes - The account tree nodes to convert
 */
export const convertForecastNodesToGridRows = (
  layoutItem: UKForecastLayoutItem,
  section: REPORT_SECTION,
  layoutNodeIndex: Record<string, ForecastTreeNode>,
  nodes: ForecastTreeNode[]
) => {
  const { category, options } = layoutItem;
  return flattenTree(nodes)
    .map(n => {
      // Specific case where we need to display node of type top-level-static inside of data grid.
      if (
        n.static_id === 'current_year_earnings' &&
        isTopLevelStaticRowType(n.type) &&
        category === CATEGORY_NAME_INDEXES.equity
      ) {
        return convertTreeNodeToTopLevelStaticGridRow(n, section, category, { isBold: false });
      }

      if (n.type === 'account' || n.type === 'eliminated-account') {
        return convertTreeNodeToAccountGridRow(n, section, category, options);
      }

      if (n.type === 'group' || n.type === 'group-by-account-name' || n.type === 'group-by-org-group-name') {
        return convertTreeNodeToGroupGridRow(n, section, category, options);
      }

      if (n.type === 'organisation-static' || n.type === 'elimination-group') {
        return convertTreeNodeToOrganisationStaticGridRow(n, section, category, options);
      }

      if (n.type === 'assumption' || n.type === 'system-assumption') {
        const parentNode = n.parentRef ? layoutNodeIndex[n.parentRef] : undefined;
        if ((n.assumption_type as string) === 'SheetsAssumption') {
          return convertTreeNodeToSheetAssumptionGridRow(n, parentNode, section, category, options);
        } else {
          return convertTreeNodeToAssumptionGridRow(n, parentNode, section, category, options);
        }
      }
    })
    .filter(notEmpty);
};

/**
 * Takes a tree node and converts it to an account grid row
 * @param node - The tree node to convert
 * @param section - The report section this row belongs to
 * @param category - The category of the account
 */
export const convertTreeNodeToAccountGridRow = (
  node: ForecastTreeNode,
  section: REPORT_SECTION,
  category: CATEGORY_NAME_INDEXES,
  options?: Partial<RowStyleOptions>
): AccountRow => {
  return {
    isFirst: node.isFirst,
    isLast: node.isLast,
    section,
    padding: false,
    level: node.level + (options?.startingLevel || 0),
    kind: ROW_KIND.account,
    childCount: node.childCount,
    account: {
      index: node.ref,
      name: node.name,
      type: ROW_KIND.account,
      canonical_category: category,
      period_to_date: node.period_to_date,
      period_data: node.row || [],
      unused_account: false,
      average_days_to_pay: node.average_days_to_pay || 0,
      system_account_role: node.system_account_role || null
    },
    ...options
  };
};

/**
 * Takes a tree node and converts it to an assumption grid row
 * @param node - The tree node to convert
 * @param section - The report section this row belongs to
 * @param category - The category of the account
 */
export const convertTreeNodeToAssumptionGridRow = (
  node: ForecastTreeNode,
  parentNode: ForecastTreeNode | undefined,
  section: REPORT_SECTION,
  category: CATEGORY_NAME_INDEXES,
  options?: Partial<RowStyleOptions>
): AssumptionRow => {
  return {
    section,
    kind: ROW_KIND.assumption,
    padding: false,
    level: node.level + (options?.startingLevel || 0),
    parentName: parentNode ? parentNode.name : '',
    siblingCount: node.siblingCount,
    siblingIndex: node.siblingIndex,
    account: {
      parent_row_id: node.parentRef || '',
      index: node.assumption_id || '',
      name: node.name,
      type: node.assumption_type!,
      canonical_category: category,
      period_data: node.row || [],
      excluded_from_report: !!node.excluded_from_report,
      is_derived: node.type === 'system-assumption' ? true : !!node.is_derived,
      paymenttype_id: '',
      paymenttype_name: '',
      paymenttype_type: '',
      imported_from_advisor: node.imported_from_advisor || false,
      is_excel_assumption: node.is_excel_assumption || null,
      primary_account_name: node.primary_account_name || null,
      this_number_explained: node.thisNumberExplained || null
    },
    isSystemAssumption: node.type === 'system-assumption',
    ...options
  };
};

/**
 * Takes a tree node and converts it to an account grid row
 * @param node - The tree node to convert
 * @param section - The report section this row belongs to
 * @param category - The category of the account
 */
export const convertTreeNodeToCategoryTotalGridRow = (
  node: ForecastTreeNode,
  section: REPORT_SECTION,
  category: CATEGORY_NAME_INDEXES,
  options?: Partial<RowStyleOptions>
): TotalRow => {
  return {
    section,
    isBold: true,
    kind: ROW_KIND.total,
    name: node.name,
    account: {
      canonical_category: category,
      index: node.ref,
      name: node.name,
      period_data: node.row || [],
      type: ROW_KIND.total,
      period_to_date: node.period_to_date,
      static_id: (node.static_id || '') as ReportTotal['static_id']
    },
    disableLargeSize: false,
    padding: false,
    childCount: node.childCount,
    level: node.level + (options?.startingLevel || 0),
    isFirst: node.isFirst,
    isLast: node.isLast,
    forceRowSize: null,
    hidePeriodToDateDifference: false,
    ...options
  };
};

/**
 * Takes a tree node and converts it to a group grid row
 * @param node - The top level static (category total) tree node to convert
 * @param joinedNode - The top level static (category total) tree node to compare figures to
 * @param section - The report section this row belongs to
 * @param category - The category of the account
 */
export const convertTreeNodeToComparisonGridRow = (
  node: ForecastTreeNode,
  joinedNode: ForecastTreeNode | undefined,
  section: REPORT_SECTION,
  category: CATEGORY_NAME_INDEXES,
  options?: Partial<RowStyleOptions>
): CalculationRow => {
  const categoryTotalRow = convertTreeNodeToCategoryTotalGridRow(node, section, category);
  const categoryJoinedTotalRow = joinedNode
    ? convertTreeNodeToCategoryTotalGridRow(joinedNode, section, category)
    : undefined;

  return {
    ...categoryTotalRow,
    childCount: 0,
    hidePeriodToDateDifference: true,
    isBold: true,
    forceRowSize: SIZES.MEDIUM,
    isFirst: false,
    isLast: false,
    percentage: options?.percentage || false,
    padding: false,
    kind: ROW_KIND.calculation,
    joinedRow: categoryJoinedTotalRow && {
      ...categoryJoinedTotalRow,
      hidePeriodToDateDifference: false,
      kind: ROW_KIND.calculation,
      percentage: true
    },
    level: 0,
    ...options
  };
};

/**
 * Takes a tree node and converts it to a group grid row
 * @param node - The tree node to convert
 * @param section - The report section this row belongs to
 * @param category - The category of the account
 */
export const convertTreeNodeToGroupGridRow = (
  node: ForecastTreeNode,
  section: REPORT_SECTION,
  category: CATEGORY_NAME_INDEXES,
  options?: Partial<RowStyleOptions>
): GroupRow => {
  return {
    section,
    isFirst: node.isFirst,
    isLast: node.isLast,
    kind: ROW_KIND.group,
    childCount: node.childCount,
    padding: false,
    level: node.level + (options?.startingLevel || 0),
    isBold: true,
    account: {
      index: node.ref,
      name: node.name,
      type: ROW_KIND.group,
      canonical_category: category,
      period_to_date: node.period_to_date,
      period_data: node.row || []
    },
    ...options
  };
};

/**
 * Takes a tree node and converts it to a organisation-static grid row
 * @param node - The tree node to convert
 * @param section - The report section this row belongs to
 * @param category - The category of the account
 */
export const convertTreeNodeToOrganisationStaticGridRow = (
  node: ForecastTreeNode,
  section: REPORT_SECTION,
  category: CATEGORY_NAME_INDEXES,
  options?: Partial<RowStyleOptions>
): OrganisationRow => {
  return {
    section,
    isFirst: node.isFirst,
    isLast: node.isLast,
    kind: ROW_KIND.organisation,
    childCount: node.childCount,
    padding: false,
    level: node.level + (options?.startingLevel || 0),
    isBold: true,
    account: {
      index: node.ref,
      name: node.name,
      type: ROW_KIND.group,
      canonical_category: category,
      period_to_date: node.period_to_date,
      period_data: node.row || []
    },
    ...options
  };
};

/**
 * Takes a tree node and converts it to a group grid row
 * @param node - The tree node to convert
 * @param section - The report section this row belongs to
 * @param category - The category of the account
 */
export const convertTreeNodeToTopLevelStaticGridRow = (
  node: ForecastTreeNode,
  section: REPORT_SECTION,
  category: CATEGORY_NAME_INDEXES,
  options?: Partial<RowStyleOptions>
): OrganisationRow => {
  return {
    section,
    isFirst: node.isFirst,
    isLast: node.isLast,
    kind: ROW_KIND.organisation,
    childCount: node.childCount,
    padding: false,
    level: node.level + (options?.startingLevel || 0),
    isBold: true,
    account: {
      index: node.ref,
      name: node.name,
      type: ROW_KIND.group,
      canonical_category: category,
      period_to_date: node.period_to_date,
      period_data: node.row || []
    },
    ...options
  };
};

/**
 * Takes a tree node and converts it to a group grid row
 * @param node - The tree node to convert
 * @param section - The report section this row belongs to
 * @param category - The category of the account
 */
export const convertTreeNodeToSheetAssumptionGridRow = (
  node: ForecastTreeNode,
  parentNode: ForecastTreeNode | undefined,
  section: REPORT_SECTION,
  category: CATEGORY_NAME_INDEXES,
  options?: Partial<RowStyleOptions>
): SheetsAssumptionRow => {
  return {
    section,
    kind: ROW_KIND.sheetsAssumption,
    padding: false,
    level: node.level + (options?.startingLevel || 0),
    parentName: parentNode?.name || '',
    siblingCount: node.siblingCount,
    siblingIndex: node.siblingIndex,
    account: {
      parent_row_id: node.parentRef || '',
      index: node.assumption_id || '',
      name: node.name,
      type: 'SheetsAssumption',
      canonical_category: category,
      period_data: node.row || [],
      is_derived: node.type === 'system-assumption' ? true : !!node.is_derived
    },
    ...options
  };
};
